import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authFor } from '../../lib/sessionLink';
import {
  startMeetingRecording, putChunkById, pauseRecording, resumeRecording, finishById, getRecording,
  roomNotReady, reasonOf, clockOf, RECORDING_LANGUAGES, MIXED_HINT, transcriptWords
} from '../../lib/recordings';
import { platformWords, cancelMeeting } from '../../lib/meetings';
import { listDevices, nameOf, onDeviceChange, remembered, remember, resolve, isGone } from '../../lib/devices';
import { pickMime } from '../recording/Recorder';
import { whenOf } from '../recording/RecordingsPage';
import { Dialog } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import SpeakerNaming from './RoomSpeakers';
import AdhocDetails from './AdhocDetails';
import { useConsentRule, consentWords } from './StartNow';
import { captureSupported, captureHint, openCallCapture, LevelMeter } from './CallCapture';
import './RoomRecorder.css';

/**
 * RECORDING A MEETING IN THE ROOM (Gordon, 22 October).
 *
 * "When there is a physical meeting I have the option to use Jarvis to
 * record the meeting while in the conference room and for the meeting to be
 * transcribed, minuted & summarized with the list of to-dos... could be a
 * phone or a laptop and account for a break/pause... since 1 recording
 * device will be used - the app distinguishes voices as Person 1, Person 2,
 * etc so the organiser can assign names later."
 *
 * So: one device on the table, one microphone, one screen that says plainly
 * what it is doing from across the room. Everything on it is large because
 * the person using it is a metre away and glancing. Before: who is here,
 * which microphone, a meter that proves the room is being heard, and one
 * red button. During: a clock, the meter, "saved up to 12:40", PAUSE and
 * STOP. After: "transcribing, you can close this", and - because the
 * organiser is usually still holding the phone when the transcript lands -
 * the "who is Speaker 1?" step right here (RoomSpeakers.js), as well as on
 * the recording's own page.
 *
 * THE CLOCK EXCLUDES BREAKS. Pause stops the MediaRecorder (pause(), so the
 * file has no silence in it), stops the clock and tells the server; Resume
 * restarts all three. The duration sent with `finish` is the recorded time,
 * so a two-hour meeting with a twenty-minute break is a 1 h 40 recording,
 * which is what the transcript's timestamps run to.
 *
 * CHUNKS GO UP AS THEY COME, every five seconds, numbered, one at a time, by
 * the uploader below - so a three-hour meeting is never three hours of audio
 * held in memory waiting for a Stop that a dead battery might not allow. A
 * chunk the server did not take is tried again, with a pause that grows to
 * half a minute and never gives up on a network fault: the wifi in a meeting
 * room comes and goes, the recording carries on regardless, and the screen
 * says "connection lost - still recording, will catch up" rather than
 * anything alarming. Only a refusal (403, 404, 410, 413) is final.
 *
 * THE MICROPHONE IS OPENED FOR A ROOM, NOT A CALL (roomAudio below): echo
 * cancellation OFF - it exists to subtract the far end of a call from what
 * the microphone hears, there is no far end here, and on a room microphone it
 * eats the quieter voices at the other end of the table; automatic gain ON so
 * those voices are lifted; noise suppression ON for the air conditioning;
 * one channel, because a phone has one microphone and the transcriber wants
 * mono anyway. The same stream drives the level meter and the recorder, so
 * there is one capture and the browser's red dot means one thing.
 *
 * THE SCREEN STAYS AWAKE while recording and through a break, with
 * navigator.wakeLock where the browser has it (Chrome, Edge, Safari 16.4+).
 * A phone that locks itself ten minutes into the meeting is the way this
 * feature fails in practice, so the screen says whether it is being held. A
 * locked phone, or the app put in the background, is the end of the useful
 * recording on iOS - Safari suspends the page's media capture - and it is
 * said in words on the recording screen.
 *
 * Mounted by MeetingPage at /meetings/:id/record with the meeting in hand;
 * given none, it reads GET /meetings/:id itself, so a reload mid-setup lands
 * on the same screen.
 *
 * TWO MORE WAYS IN (Gordon, 26 October, migration 090):
 *
 *   AN AD-HOC MEETING ("Start meeting now") arrives here with nothing but a
 *   title made of the time. Its details - title, who is here (staff from
 *   the directory, anybody else by name), and for a call which app - are a
 *   card on this screen (AdhocDetails.js), before, DURING and after the
 *   recording; saving them is one PATCH and never touches the recorder.
 *
 *   A CALL ON ANOTHER APP (kind 'call') records the microphone MIXED with
 *   the other app's sound (CallCapture.js): the person confirms the firm's
 *   consent reminder, chooses what to capture in the browser's own dialog,
 *   sees a meter for each side - and is warned, plainly, when the other side
 *   is not there, because a transcript of half a call is the way this fails
 *   in practice. Video is never kept. What was mixed goes to the server as
 *   `capture`, the confirmation as `consent_confirmed`.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const TIMESLICE_MS = 5000;
const HEARD_LEVEL = 0.08;      // the meter's level that counts as a voice
const MAX_BACKOFF_MS = 30000;

const NOT_SUPPORTED = 'This browser cannot record sound. Chrome, Edge, Firefox and Safari 14.1 or later can; '
  + 'on an iPhone, open Jarvis in Safari or from the home screen.';
const IS_IOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent || '');

/* ------------------------------------------------------------- the capture
 *
 * One of these for the page. The stream is opened for the meter the moment
 * the screen is up; Record hands the same stream to a MediaRecorder.
 */
const room = {
  stream: null, recorder: null, recordingId: null, mime: null, seq: 0,
  startedAt: 0, pausedMs: 0, pauseAt: 0, wake: null,
  // A call on another app: the microphone and the captured sound are mixed
  // into `stream`; these are the originals, stopped with it, and the mixer.
  mic: null, sources: [], closeCapture: null
};

/** Let go of everything open: the mixed stream, its sources, the mixer. */
const releaseAll = () => {
  try { room.stream?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
  for (const s of room.sources) { try { s?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ } }
  try { room.closeCapture?.(); } catch { /* closed */ }
  room.stream = null; room.mic = null; room.sources = []; room.closeCapture = null;
};

/** The microphone, asked for the way a room wants it (see the header). */
export const roomAudio = (deviceId) => ({
  channelCount: 1,
  echoCancellation: false,
  noiseSuppression: true,
  autoGainControl: true,
  ...(deviceId ? { deviceId: { exact: deviceId } } : {})
});

/** Whether this browser can do the job at all. */
const canRecord = () => typeof window !== 'undefined' && Boolean(window.MediaRecorder)
  && Boolean(navigator.mediaDevices?.getUserMedia);

/* ------------------------------------------------------------ the uploader
 *
 * Module-level, as the call's is (recording/store.js): the last chunks and
 * the finish must reach the server after Stop even if the organiser leaves
 * the page before they have. The state it publishes is small, and read by
 * the screen with useSyncExternalStore.
 *
 *   pending     chunks not yet on the server
 *   savedToMs   the recorded clock at the end of the last chunk the server took
 *   lost        a chunk is being retried after a network fault
 *   failed      a chunk was refused for good (the recording has a hole)
 *   finished    finish has been accepted; null, or the recording the server sent
 */
let upState = { pending: 0, savedToMs: 0, lost: false, failed: false, finished: null, finishing: false };
const upListeners = new Set();
const publish = (patch) => {
  upState = { ...upState, ...patch };
  upListeners.forEach((fn) => { try { fn(); } catch { /* a listener must not stop an upload */ } });
};
const subscribeUp = (fn) => { upListeners.add(fn); return () => upListeners.delete(fn); };
const getUp = () => upState;
const useUploads = () => useSyncExternalStore(subscribeUp, getUp, getUp);

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const FINAL = [403, 404, 410, 413];

class RoomUploader {
  constructor() { this.queue = []; this.running = false; this.finish = null; }

  reset() {
    this.queue = []; this.finish = null;
    publish({ pending: 0, savedToMs: 0, lost: false, failed: false, finished: null, finishing: false });
  }

  add(job) {
    this.queue.push(job);
    publish({ pending: this.queue.length });
    this.run();
  }

  /** Called once the last chunk is queued: finish follows the queue. */
  end(recordingId, durationMs) {
    this.finish = { recordingId, durationMs };
    publish({ finishing: true });
    this.run();
  }

  get pending() { return this.queue.length; }

  async run() {
    if (this.running) return;
    this.running = true;
    try {
      while (this.queue.length) {
        const job = this.queue[0];
        let ok = false;
        let attempt = 0;
        while (!ok) {
          try {
            await putChunkById(job.recordingId, job.seq, job.blob, job.mime);
            ok = true;
          } catch (err) {
            const status = err.response?.status;
            if (status === 409 && Number.isInteger(err.response?.data?.expected)) {
              // A number was skipped; the server says which it wants.
              job.seq = err.response.data.expected;
              continue;
            }
            if (FINAL.includes(status)) break;
            // Anything else - no answer at all, a 5xx, a 429 - is the network
            // or the server having a moment, and a meeting does not stop for
            // either. Wait, longer each time, up to half a minute, and go on.
            attempt += 1;
            publish({ lost: true });
            await wait(Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempt - 1, 5)));
          }
        }
        this.queue.shift();
        publish({ pending: this.queue.length, lost: false,
          ...(ok ? { savedToMs: Math.max(upState.savedToMs, job.endMs || 0) } : { failed: true }) });
      }
      if (this.finish) {
        const f = this.finish;
        this.finish = null;
        let rec = null;
        try { rec = await finishById(f.recordingId, f.durationMs); } catch { /* the server times it out itself */ }
        publish({ finishing: false, finished: rec || { id: f.recordingId, state: 'transcribing' } });
      }
    } finally {
      this.running = false;
      if (this.queue.length || this.finish) this.run();
    }
  }
}

export const roomUploader = new RoomUploader();

/* Leaving with chunks in flight loses them; only the browser's own dialog can
   hold the tab. Installed once, asks only while something is pending. */
if (typeof window !== 'undefined' && !window.__jarvisRoomGuard) {
  window.__jarvisRoomGuard = true;
  window.addEventListener('beforeunload', (e) => {
    if (roomUploader.pending > 0 || roomUploader.finish || room.recorder) {
      e.preventDefault();
      e.returnValue = 'The meeting recording is still being saved.';
      return e.returnValue;
    }
    return undefined;
  });
}

/* ------------------------------------------------------------- the capture */

/** The recorded clock: wall time since start, minus every break. */
export const recordedMs = () => {
  if (!room.startedAt) return 0;
  const end = room.pauseAt || Date.now();
  return Math.max(0, end - room.startedAt - room.pausedMs);
};

function startCapture(recordingId, mime) {
  const rec = new window.MediaRecorder(room.stream, mime ? { mimeType: mime } : undefined);
  room.recorder = rec;
  room.recordingId = recordingId;
  room.mime = mime;
  room.seq = 0;
  room.pausedMs = 0;
  room.pauseAt = 0;
  roomUploader.reset();
  rec.ondataavailable = (e) => {
    if (!e.data || e.data.size === 0) return;
    roomUploader.add({ recordingId, seq: room.seq, blob: e.data, mime, endMs: recordedMs() });
    room.seq += 1;
  };
  rec.onstop = () => {
    // ondataavailable has fired with the tail before onstop; finish follows it.
    roomUploader.end(recordingId, recordedMs());
    room.recorder = null;
    room.startedAt = 0;
    room.pauseAt = 0;
    room.pausedMs = 0;
    // The microphone (and a call's captured sound) is let go here, so the
    // browser's red dot goes out with the recording and not a moment later.
    releaseAll();
  };
  rec.onerror = () => { try { rec.stop(); } catch { /* already gone */ } };
  room.startedAt = Date.now();
  rec.start(TIMESLICE_MS);
}

/** Stop recording: the tail chunk goes up, then finish. Safe to call twice. */
export function stopRoomCapture() {
  const rec = room.recorder;
  if (!rec || rec.state === 'inactive') return false;
  try { rec.stop(); } catch { room.recorder = null; }
  return true;
}

/* ------------------------------------------------------------ keeping awake */

async function holdAwake() {
  const wl = typeof navigator !== 'undefined' ? navigator.wakeLock : null;
  if (!wl || typeof wl.request !== 'function') return 'unavailable';
  try {
    const sentinel = await wl.request('screen');
    room.wake = sentinel;
    // The browser lets go whenever the page is hidden; forgetting the spent
    // sentinel is what lets the visibility handler take a fresh one.
    if (typeof sentinel.addEventListener === 'function') {
      sentinel.addEventListener('release', () => { if (room.wake === sentinel) room.wake = null; });
    }
    return 'held';
  } catch {
    // Refused: battery saver on, or a page that is not visible. Said, not hidden.
    return 'refused';
  }
}

function letSleep() {
  const w = room.wake;
  room.wake = null;
  if (w && typeof w.release === 'function') w.release().catch(() => {});
}

/* --------------------------------------------------------------- helpers */

/** The meeting's attendees as names, whatever shape the server sent them in. */
const namesOf = (meeting) => {
  const rows = Array.isArray(meeting?.attendees) ? meeting.attendees : [];
  const out = [];
  for (const a of rows) {
    const n = typeof a === 'string' ? a : (a?.name || a?.display_name || a?.email || '');
    const s = String(n || '').trim();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
};

const Dot = ({ className = '' }) => <span className={`rr-dot ${className}`} aria-hidden="true" />;

/* --------------------------------------------------------------- the screen */

export default function RoomRecorder({ meeting: given = null }) {
  const { id: routeId } = useParams();
  const location = useLocation();
  const [params] = useSearchParams();
  // Start meeting now / Record a call hand the meeting over in the route's
  // state, so the screen is up before GET /meetings/:id answers; a reload has
  // no state and reads it.
  const handed = location.state?.meeting && location.state.meeting.id === routeId ? location.state.meeting : null;
  const handedRef = useRef(handed);
  const [meeting, setMeeting] = useState(given || handed);
  const [meetingTrouble, setMeetingTrouble] = useState(null);
  const supported = useMemo(canRecord, []);
  // 'ready' | 'starting' | 'recording' | 'paused' | 'stopping' | 'done'
  const [phase, setPhase] = useState('ready');
  const [names, setNames] = useState(() => namesOf(given || handed));
  const [adding, setAdding] = useState('');
  // A call on another app (090): the firm's rule, the person's confirmation
  // (carried in from the dialog as ?consent=1, asked again on a reload), and
  // what the browser captured - null until chosen.
  const isCall = meeting?.kind === 'call';
  const adhoc = meeting?.adhoc === true;
  const rule = useConsentRule();
  const [consented, setConsented] = useState(() => params.get('consent') === '1' || location.state?.consent === true);
  const [micStream, setMicStream] = useState(null);
  const [call, setCall] = useState(null); // { remote, capture, display }
  const [capturing, setCapturing] = useState(false);
  const [captureNote, setCaptureNote] = useState(null);
  const [micOnlyOk, setMicOnlyOk] = useState(false);
  const [remoteHeard, setRemoteHeard] = useState(false);
  const [list, setList] = useState(null);
  const [choice, setChoice] = useState(() => remembered());
  const [language, setLanguage] = useState('');
  const [heard, setHeard] = useState(false);
  const [micNote, setMicNote] = useState(null);
  const [trouble, setTrouble] = useState(null);
  const navigate = useNavigate();
  // "Start meeting now" made the meeting at the press; Cancel, before anything
  // is recorded, cancels it again and goes back (Gordon, 29 October).
  const [dropping, setDropping] = useState(false);
  const dropAdhoc = async () => {
    if (!meeting?.id) { navigate('/meetings'); return; }
    setDropping(true);
    try {
      await cancelMeeting(meeting.id);
      navigate('/meetings', { replace: true });
    } catch (err) {
      setDropping(false);
      setTrouble(err.response?.data?.error || 'Could not cancel the meeting.');
    }
  };
  const [recordingId, setRecordingId] = useState(null);
  const [clock, setClock] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [wake, setWake] = useState('none'); // 'none' | 'held' | 'unavailable' | 'refused'
  const [after, setAfter] = useState(null);  // the recording, read back while it transcribes
  const up = useUploads();
  const meterRef = useRef(null);
  const addRef = useRef(null);
  const live = phase === 'recording' || phase === 'paused';

  /* ---- the meeting, when the page was reached without one (or handed one
          that may be a moment old: read again, keep what was handed meanwhile) */
  useEffect(() => {
    if (given) { setMeeting(given); setNames(namesOf(given)); return undefined; }
    if (!routeId) return undefined;
    let gone = false;
    axios.get(`${API_BASE}/meetings/${routeId}`, authFor()).then((r) => {
      if (gone) return;
      setMeeting(r.data);
      setNames(namesOf(r.data));
    }).catch((err) => {
      if (handedRef.current) return; // the meeting in hand is good enough to record on
      if (gone) return;
      setMeetingTrouble(roomNotReady(err) ? 'Meetings are not switched on for this server yet.'
        : reasonOf(err, 'Could not open that meeting.'));
    });
    return () => { gone = true; };
  }, [given, routeId]);

  /* ---- the microphones */
  const refresh = useCallback(async () => { setList(await listDevices()); }, []);
  useEffect(() => { refresh(); return onDeviceChange(refresh); }, [refresh]);
  const mic = resolve('audioinput', list, choice);

  /*
   * THE STREAM AND THE METER. Opened as soon as the screen is up, on the
   * chosen microphone, and kept in `room.stream` for Record to use. The
   * analyser paints the bar straight onto the element, outside React, and
   * flips `heard` once when a voice has been seen - that is the "we can hear
   * you" line. Changing the microphone (only possible before Record) opens
   * the new one and stops the old.
   */
  useEffect(() => {
    if (!supported || live) return undefined;
    let stopped = false;
    let own = null;
    let ctx = null;
    let frame = 0;
    const bar = meterRef.current;
    (async () => {
      try {
        own = await navigator.mediaDevices.getUserMedia({ audio: roomAudio(mic.deviceId), video: false });
      } catch (err) {
        if (stopped) return;
        if (isGone(err) && mic.deviceId) {
          const was = choice?.audioinput?.label;
          setMicNote(`${was ? `"${was}"` : 'The remembered microphone'} is not plugged in — using the system default.`);
          try { own = await navigator.mediaDevices.getUserMedia({ audio: roomAudio(null), video: false }); } catch { return; }
        } else if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
          setMicNote('The microphone is blocked for Jarvis. Allow it in the browser’s address bar, then try again.');
          return;
        } else {
          setMicNote('No microphone could be opened on this device.');
          return;
        }
      }
      if (stopped) { own.getTracks().forEach((t) => t.stop()); return; }
      room.mic = own;
      // A call's mix, once chosen, is what is recorded; the microphone alone
      // until then (and always, for a meeting in the room).
      if (!room.closeCapture) room.stream = own;
      setMicStream(own);
      setMicNote(null);
      refresh(); // the permission just granted is what fills in the names
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor || !bar) return;
      try {
        ctx = new Ctor();
        const source = ctx.createMediaStreamSource(own);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const data = new Uint8Array(analyser.fftSize);
        let heardYet = false;
        const tick = () => {
          if (stopped) return;
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i += 1) { const v = (data[i] - 128) / 128; sum += v * v; }
          // RMS, lifted: a raw 0.05 is a normal voice and would be a 5% bar.
          const level = Math.min(1, Math.sqrt(sum / data.length) * 4);
          bar.style.width = `${Math.round(level * 100)}%`;
          bar.parentElement?.setAttribute('data-level', String(Math.round(level * 100)));
          if (!heardYet && level > HEARD_LEVEL) { heardYet = true; setHeard(true); }
          frame = requestAnimationFrame(tick);
        };
        tick();
      } catch { /* no Web Audio: the bar stays flat, the recording is unaffected */ }
    })();
    return () => {
      stopped = true;
      if (frame) cancelAnimationFrame(frame);
      try { ctx?.close(); } catch { /* already closed */ }
      // Record took the stream (or mixed it into a call's): the recorder owns
      // it now and stops it at the end.
      if (own && room.recorder && (room.stream === own || room.sources.includes(own))) return;
      // Mixed into a call's capture that is still being set up: the mixer owns it.
      if (own && room.closeCapture && room.sources.includes(own)) return;
      try { own?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
      if (room.stream === own) room.stream = null;
      if (room.mic === own) room.mic = null;
      if (bar && document.contains(bar)) bar.style.width = '0%';
    };
    // The label is only for the note; the deviceId is what opens a stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, live, mic.deviceId, refresh]);

  /* The meter while recording: the same stream, a new analyser (the one above
     went with its effect). Kept apart so changing nothing re-opens nothing. */
  useEffect(() => {
    if (!live || !room.stream) return undefined;
    let stopped = false;
    let ctx = null;
    let frame = 0;
    const bar = meterRef.current;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor || !bar) return undefined;
    try {
      ctx = new Ctor();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(room.stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        if (stopped) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) { const v = (data[i] - 128) / 128; sum += v * v; }
        const level = phase === 'paused' ? 0 : Math.min(1, Math.sqrt(sum / data.length) * 4);
        bar.style.width = `${Math.round(level * 100)}%`;
        bar.parentElement?.setAttribute('data-level', String(Math.round(level * 100)));
        frame = requestAnimationFrame(tick);
      };
      tick();
    } catch { /* flat bar */ }
    return () => {
      stopped = true;
      if (frame) cancelAnimationFrame(frame);
      try { ctx?.close(); } catch { /* closed */ }
    };
  }, [live, phase]);

  /* ---- the clock, four times a second while recording */
  useEffect(() => {
    if (!live) return undefined;
    setClock(recordedMs());
    const t = setInterval(() => setClock(recordedMs()), 250);
    return () => clearInterval(t);
  }, [live]);

  /* ---- the wake lock, re-taken when the screen comes back (the browser
          lets go of it whenever the page is hidden) */
  useEffect(() => {
    if (!live) return undefined;
    const onVis = async () => {
      if (document.visibilityState !== 'visible' || room.wake) return;
      setWake(await holdAwake());
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [live]);

  /* ---- finish accepted: the screen moves on */
  useEffect(() => {
    if (phase === 'stopping' && up.finished) {
      setAfter(up.finished);
      setPhase('done');
    }
  }, [phase, up.finished]);

  /* ---- afterwards: look again every five seconds until the transcript lands,
          so the organiser can name the voices without leaving this screen */
  useEffect(() => {
    if (phase !== 'done' || !recordingId) return undefined;
    const settled = (r) => (Array.isArray(r?.segments) && r.segments.length > 0)
      || /Failed|No transcript/.test(transcriptWords(r));
    let gone = false;
    let t = null;
    const look = () => getRecording(recordingId).then((r) => {
      if (gone) return;
      setAfter(r);
      if (settled(r) && t) clearInterval(t);
    }).catch(() => {});
    look();
    t = setInterval(look, 5000);
    return () => { gone = true; clearInterval(t); };
  }, [phase, recordingId]);

  /* Leaving the screen ends the recording - the phone IS the recorder - and
     the uploader sees the tail through. A capture chosen and never recorded
     is let go of here too. */
  useEffect(() => () => { if (!stopRoomCapture()) releaseAll(); letSleep(); }, []);

  /* ---- a call on another app: what to capture */
  const dropCapture = useCallback(() => {
    try { room.closeCapture?.(); } catch { /* closed */ }
    for (const s of room.sources) { if (s !== room.mic) { try { s?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ } } }
    room.closeCapture = null; room.sources = []; room.stream = room.mic;
    setCall(null);
    setRemoteHeard(false);
  }, []);

  const chooseCapture = async () => {
    setCaptureNote(null);
    setCapturing(true);
    try {
      if (!room.mic) {
        room.mic = await navigator.mediaDevices.getUserMedia({ audio: roomAudio(mic.deviceId), video: false });
        setMicStream(room.mic);
      }
      dropCapture();
      const got = await openCallCapture(room.mic, {
        onEnded: () => {
          // The person pressed the browser's own "Stop sharing": the other
          // side has gone from the mix. Said, not hidden; the recording of
          // the microphone carries on.
          setCaptureNote('The browser stopped sharing the call’s sound - only your microphone is being recorded now. Press Choose again to pick it up.');
          setCall((c) => (c ? { ...c, remote: false, capture: 'mic' } : c));
        }
      });
      room.stream = got.mixed;
      room.sources = [room.mic, got.display];
      room.closeCapture = got.close;
      setCall({ remote: got.remote, capture: got.capture, display: got.remote ? new MediaStream(got.display.getAudioTracks()) : null });
      if (!got.remote) setCaptureNote('No sound was shared. Only your microphone would be recorded — the other side would not be in the transcript.');
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') setCaptureNote('Nothing was chosen. Choose the screen or tab the call is in, and tick its audio.');
      else if (err?.name === 'NotSupportedError' || err?.name === 'TypeError') setCaptureNote(`This browser cannot capture another app’s sound here. ${captureHint()}`);
      else setCaptureNote(reasonOf(err, 'Could not capture the call’s sound.'));
    } finally {
      setCapturing(false);
    }
  };

  /* ---- attendees */
  const addName = () => {
    const n = adding.trim();
    if (!n) return;
    setNames((cur) => (cur.includes(n) ? cur : [...cur, n]));
    setAdding('');
    addRef.current?.focus();
  };
  const dropName = (n) => setNames((cur) => cur.filter((x) => x !== n));

  const pickMic = (e) => {
    const id = e.target.value;
    const device = id ? (list?.audioinput || []).find((d) => d.deviceId === id) : null;
    setMicNote(null);
    setHeard(false);
    // A new microphone means a new mix: the capture is chosen again.
    if (room.closeCapture) dropCapture();
    setChoice(remember('audioinput', device ? { deviceId: id, label: device.label } : null));
  };

  /** The ad-hoc details saved: the meeting on screen follows, and so does the list of names for the recording. */
  const detailsSaved = (m) => {
    setMeeting((cur) => ({ ...(cur || {}), ...(m || {}) }));
    setNames(namesOf(m));
  };

  /* ---- record, pause, resume, stop */
  const begin = async () => {
    if (!meeting?.id) return;
    const mime = pickMime('audio');
    if (!mime) { setTrouble(NOT_SUPPORTED); return; }
    if (isCall && !consented) { setTrouble(`Confirm first that the people on the call know it is being recorded. ${consentWords(rule, 'call')}`); return; }
    if (isCall && !call?.remote && !micOnlyOk) { setTrouble('Choose what to capture, or tick “Record my microphone only” to go on without the other side.'); return; }
    setTrouble(null);
    setPhase('starting');
    try {
      if (!room.stream) {
        // Refused earlier, or not asked yet: ask now, from the button press.
        room.mic = await navigator.mediaDevices.getUserMedia({ audio: roomAudio(mic.deviceId), video: false });
        room.stream = room.mic;
        setMicStream(room.mic);
        setMicNote(null);
      }
      const r = await startMeetingRecording(meeting.id, {
        kind: 'audio', mime, language: language || null, attendees: names,
        ...(isCall ? { consentConfirmed: true, capture: call?.remote ? call.capture : 'mic' } : {})
      });
      startCapture(r?.id, mime);
      setRecordingId(r?.id || null);
      setPhase('recording');
      setWake(await holdAwake());
    } catch (err) {
      setPhase('ready');
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setMicNote('The microphone is blocked for Jarvis. Allow it in the browser’s address bar, then try again.');
        return;
      }
      // Not the server at all: the MediaRecorder itself refused the stream or
      // the mime (NotSupportedError), which is the browser's doing.
      if (!axios.isAxiosError(err)) { setTrouble(NOT_SUPPORTED); return; }
      const status = err.response?.status;
      if (err.response?.data?.consent_required) { setConsented(false); setTrouble(`Confirm first that the people on the call know it is being recorded. ${consentWords(err.response.data.consent || rule, 'call')}`); return; }
      setTrouble(roomNotReady(err) ? 'Room recordings are not switched on for this server yet - it needs migration 066.'
        : status === 503 ? reasonOf(err, 'Recording storage is not set up on this server.')
          : status === 403 ? reasonOf(err, 'Recording is not allowed for you on this meeting.')
            : !err.response ? 'The server could not be reached. Check the connection and try again.'
              : reasonOf(err, 'Could not start recording.'));
    }
  };

  const pause = () => {
    const rec = room.recorder;
    if (!rec || rec.state !== 'recording') return;
    try { rec.pause(); } catch { return; }
    room.pauseAt = Date.now();
    setClock(recordedMs());
    setPhase('paused');
    // Bookkeeping on the server; the break is real whether or not it hears.
    if (recordingId) pauseRecording(recordingId).catch(() => {});
  };

  const resume = () => {
    const rec = room.recorder;
    if (!rec || rec.state !== 'paused') return;
    room.pausedMs += Date.now() - room.pauseAt;
    room.pauseAt = 0;
    try { rec.resume(); } catch { return; }
    setPhase('recording');
    if (recordingId) resumeRecording(recordingId).catch(() => {});
  };

  const stop = () => {
    setConfirming(false);
    if (!room.recorder) return;
    setPhase('stopping');
    letSleep();
    setWake('none');
    stopRoomCapture();
  };

  /* ---------------------------------------------------------------- render */

  const title = meeting?.title || meeting?.subject || 'Meeting';
  const when = whenOf(meeting?.starts_at || meeting?.start_at || meeting?.scheduled_at);
  const micRows = list?.audioinput || [];
  const kicker = isCall ? `Record a call${meeting?.platform ? ` on ${platformWords(meeting.platform) || meeting.platform}` : ' on another app'}`
    : adhoc ? 'Meeting now' : 'Record in the room';
  const peopleCount = (meeting?.attendees || []).filter((a) => a.role !== 'organiser').length;
  // The details card for an ad-hoc meeting: open before and after, folded
  // under one line while recording so the clock stays the biggest thing.
  const details = adhoc && meeting?.id ? (
    live || phase === 'stopping' ? (
      <details className="rr-card rr-fold" data-details-fold="">
        <summary className="rr-fold-summary">
          <Icon name="edit" size={14} /> Details — {title}{peopleCount > 0 ? ` · ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}` : ' · who is here?'}
        </summary>
        <AdhocDetails meeting={meeting} onSaved={detailsSaved} live />
      </details>
    ) : (
      <section className="rr-card" aria-labelledby="rr-details-h">
        <h2 className="rr-h2" id="rr-details-h">{phase === 'done' ? 'What was this?' : 'What is this?'}</h2>
        <p className="rr-hint">A title and who is here. It can wait until afterwards - nothing stops for it.</p>
        <AdhocDetails meeting={meeting} onSaved={detailsSaved} />
      </section>
    )
  ) : null;

  if (!supported) {
    return (
      <div className="rr-page" data-phase="unsupported">
        <header className="rr-head">
          <h1 className="rr-title">{title}</h1>
          {when && <p className="rr-when">{when}</p>}
        </header>
        <div className="rr-card rr-plain" role="alert">
          <p className="rr-big-words">{NOT_SUPPORTED}</p>
        </div>
      </div>
    );
  }

  if (meetingTrouble) {
    return (
      <div className="rr-page" data-phase="trouble">
        <div className="rr-card rr-plain" role="alert"><p className="rr-big-words">{meetingTrouble}</p></div>
      </div>
    );
  }

  /* The upload line: one sentence about where the recording is. */
  const status = (() => {
    if (up.failed) return { kind: 'bad', text: 'Part of the recording was refused by the server - there may be a gap.' };
    if (up.lost) return { kind: 'lost', text: 'Connection lost — still recording, will catch up.' };
    if (up.pending > 0) return { kind: 'busy', text: up.savedToMs > 0 ? `Uploading… saved up to ${clockOf(up.savedToMs)}` : 'Uploading…' };
    if (up.savedToMs > 0) return { kind: 'ok', text: `Saved up to ${clockOf(up.savedToMs)}` };
    return { kind: 'ok', text: 'Saving as it goes' };
  })();

  const wakeWords = wake === 'held' ? 'The screen stays awake while recording.'
    : wake === 'refused' ? 'The browser would not hold the screen awake - keep the phone from locking.'
      : wake === 'unavailable' ? 'This browser cannot hold the screen awake - keep the phone from locking.' : null;

  return (
    <div className={`rr-page is-${phase}${isCall ? ' is-call' : ''}${adhoc ? ' is-adhoc' : ''}`} data-phase={phase} data-recording={recordingId || undefined}
      data-kind={meeting?.kind || undefined} data-adhoc={adhoc || undefined}>
      <header className="rr-head">
        <p className="rr-kicker">
          {live ? <><Dot className={phase === 'paused' ? 'is-paused' : 'is-live'} /> {phase === 'paused' ? 'Paused · break' : 'Recording'}</>
            : phase === 'done' ? 'Recorded' : kicker}
        </p>
        <h1 className="rr-title">{title}</h1>
        <p className="rr-when">
          {when}
          {adhoc && <span className="rr-tag" title="Started on the spot, not planned">Ad hoc</span>}
          {isCall && <span className="rr-tag is-call">{meeting?.platform ? platformWords(meeting.platform) : 'Another app'}</span>}
        </p>
      </header>

      {trouble && (
        <p className="rr-trouble" role="alert">
          {trouble} <button type="button" className="tds-link" onClick={() => setTrouble(null)}>Dismiss</button>
        </p>
      )}

      {/* ------------------------------------------------------- before */}
      {(phase === 'ready' || phase === 'starting') && (
        <>
          {isCall && (
            <section className={`rr-card rr-consent${consented ? ' is-on' : ''}`} aria-labelledby="rr-consent-h" data-rule={rule}>
              <h2 className="rr-h2" id="rr-consent-h"><Icon name="shield" size={15} /> Before you record</h2>
              <p className="rr-hint">{consentWords(rule, 'call')} Jarvis is not in the call, so only you can.</p>
              <label className="rr-check">
                <input type="checkbox" checked={consented} onChange={(e) => { setConsented(e.target.checked); if (e.target.checked) setTrouble(null); }} data-consent="" />
                <span>{rule === 'ask' ? 'I have asked, and everyone on the call agrees to the recording' : 'I have told everyone on the call that it is being recorded'}</span>
              </label>
            </section>
          )}
          {details}
          {!adhoc && (
          <section className="rr-card" aria-labelledby="rr-who">
            <h2 className="rr-h2" id="rr-who">Attendees in the room</h2>
            <p className="rr-hint">The transcript hears voices as Speaker 1, 2, 3… These are the names you will put to them afterwards.</p>
            <ul className="rr-names">
              {names.map((n) => (
                <li key={n} className="rr-name">
                  <span className="rr-name-text">{n}</span>
                  <button type="button" className="rr-name-x" aria-label={`Remove ${n}`} onClick={() => dropName(n)}>
                    <Icon name="close" size={14} />
                  </button>
                </li>
              ))}
              {names.length === 0 && <li className="rr-names-empty">Nobody listed yet - add who is here.</li>}
            </ul>
            <form className="rr-add" onSubmit={(e) => { e.preventDefault(); addName(); }}>
              <input ref={addRef} value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="Add a name"
                aria-label="Add a name" autoComplete="off" enterKeyHint="done" />
              <button type="submit" className="btn btn-outline tds-btn" disabled={!adding.trim()}>
                <Icon name="plus" size={14} /> Add
              </button>
            </form>
          </section>
          )}

          <section className="rr-card" aria-labelledby="rr-mic">
            <h2 className="rr-h2" id="rr-mic">{isCall ? 'What to record' : 'Microphone'}</h2>
            <label className="tds-field rr-mic">
              <span>{isCall ? 'Your microphone (this side of the call)' : 'Which one'}</span>
              <select value={mic.deviceId || ''} onChange={pickMic} aria-label="Microphone">
                <option value="">System default</option>
                {micRows.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{nameOf(d, i, 'audioinput')}</option>)}
              </select>
            </label>
            <div className="rr-meter" role="img" aria-label="Microphone level" data-level="0" data-meter="mic">
              <span className="rr-meter-bar" ref={meterRef} />
            </div>
            <p className={`rr-heard${heard ? ' is-heard' : ''}`} role="status" aria-live="polite">
              {micNote ? micNote : heard
                ? <><Icon name="check" size={14} /> Test: we can hear you.</>
                : 'Test: say something, and the bar should move.'}
            </p>
            {isCall && (
              <div className={`rr-capture${call?.remote ? ' is-on' : call ? ' is-missing' : ''}`} data-capture={call ? (call.remote ? call.capture : 'none') : 'unchosen'}>
                <span className="rr-details-label">The other side of the call</span>
                <p className="rr-hint rr-capture-hint">{captureHint()}</p>
                <div className="rr-capture-row">
                  <button type="button" className={`btn ${call?.remote ? 'btn-outline' : 'btn-primary'} tds-btn rr-choose`} onClick={chooseCapture}
                    disabled={capturing || !captureSupported()}>
                    <Icon name="share" size={14} /> {capturing ? 'Choosing…' : call ? 'Choose again' : 'Choose what to capture'}
                  </button>
                  {call?.remote && (
                    <span className="rr-capture-what">
                      <Icon name="check" size={14} /> {call.capture === 'mic+tab' ? 'A browser tab’s sound' : 'The computer’s sound'} is being captured
                    </span>
                  )}
                </div>
                {call?.remote && (
                  <>
                    <LevelMeter stream={call.display} label="Level of the other side" testId="remote" onHeard={() => setRemoteHeard(true)} />
                    <p className={`rr-heard${remoteHeard ? ' is-heard' : ''}`} role="status" aria-live="polite" data-remote-heard={remoteHeard || undefined}>
                      {remoteHeard ? <><Icon name="check" size={14} /> The other side is coming through.</> : 'Let them speak: this bar should move too.'}
                    </p>
                  </>
                )}
                {(captureNote || (call && !call.remote)) && (
                  <p className="rr-capture-warn" role="alert" data-mic-only-warning="">
                    <Icon name="flag" size={14} />
                    <span>{captureNote || 'Only your microphone is being recorded — the other side won’t be in the transcript.'}</span>
                  </p>
                )}
                {(!captureSupported() || (call && !call.remote) || captureNote) && (
                  <label className="rr-check rr-mic-only">
                    <input type="checkbox" checked={micOnlyOk} onChange={(e) => setMicOnlyOk(e.target.checked)} data-mic-only="" />
                    <span>Record my microphone only{IS_IOS || !captureSupported() ? '' : ' - I know the other side will be missing'}</span>
                  </label>
                )}
              </div>
            )}
            {list && !list.labelled && !micNote && (
              <p className="rr-hint">Microphone names appear once it has been allowed.</p>
            )}
            {mic.missing && (
              <p className="rr-hint rr-gone" role="status">
                {mic.missing.label ? `"${mic.missing.label}"` : 'The remembered microphone'} is not plugged in — using the system default.
              </p>
            )}
            {/* The transcriber's language, as in a call: left alone it is the
                firm's default (Settings -> Rules; Malti + English unless
                changed); naming one language helps a meeting all in one. */}
            <label className="tds-field rr-lang">
              <span>Transcribe in</span>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label="Language of the meeting" data-rec-language="">
                <option value="">The firm&rsquo;s default</option>
                {RECORDING_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>
            {language === 'mixed' && <p className="rr-hint" data-rec-language-hint="">{MIXED_HINT}</p>}
          </section>

          <div className="rr-foot">
            <button type="button" className="rr-big rr-record" onClick={begin}
              disabled={phase === 'starting' || !meeting?.id || (isCall && (!consented || (!call?.remote && !micOnlyOk)))}
              aria-label="Record">
              <span className="rr-big-dot" aria-hidden="true" />
              <span className="rr-big-word">{phase === 'starting' ? 'Starting…' : 'RECORD'}</span>
            </button>
            <p className="rr-hint rr-foot-hint">
              {isCall
                ? (!consented ? 'Tick the confirmation above first.' : !call?.remote && !micOnlyOk ? 'Choose what to capture first, so the other side is heard.'
                  : 'Keep this tab open while the call runs; a break is a tap on Pause.')
                : <>{consentWords(rule, 'room')} Put the device in the middle of the table; the screen stays on while it records.</>}
            </p>
            {adhoc && (
              <button type="button" className="btn btn-outline tds-btn rr-cancel" data-adhoc-cancel="" disabled={dropping || phase === 'starting'}
                onClick={dropAdhoc} title="Nothing has been recorded: the meeting just made is cancelled">
                {dropping ? 'Cancelling…' : 'Cancel this meeting'}
              </button>
            )}
          </div>
        </>
      )}

      {/* ------------------------------------------------------- during */}
      {(live || phase === 'stopping') && (
        <>
          <section className="rr-card rr-live" aria-live="off">
            <p className="rr-clock" aria-label="Recorded time">{clockOf(clock)}</p>
            <p className="rr-clock-note">{phase === 'paused' ? 'The clock is stopped. Breaks are not recorded.' : 'Recorded time, breaks excluded'}</p>
            {isCall ? (
              <div className="rr-two-meters">
                <div className="rr-two-meter">
                  <span className="rr-details-label">You</span>
                  <LevelMeter stream={micStream} label="Your microphone's level" off={phase === 'paused'} testId="mic-live" />
                </div>
                <div className="rr-two-meter">
                  <span className="rr-details-label">The other side</span>
                  {call?.remote && call.display
                    ? <LevelMeter stream={call.display} label="Level of the other side" off={phase === 'paused'} testId="remote-live" />
                    : <p className="rr-capture-warn is-small" role="status" data-mic-only-warning=""><Icon name="flag" size={13} /><span>Not being recorded</span></p>}
                </div>
              </div>
            ) : (
              <div className={`rr-meter is-large${phase === 'paused' ? ' is-off' : ''}`} role="img" aria-label="Microphone level" data-level="0">
                <span className="rr-meter-bar" ref={meterRef} />
              </div>
            )}
            <p className={`rr-status is-${status.kind}`} role="status" aria-live="polite">
              {status.kind === 'lost' && <Icon name="clock" size={14} />}
              {status.text}
            </p>
            {wakeWords && <p className="rr-wake" data-wake={wake}>{wakeWords}</p>}
            {IS_IOS && (
              <p className="rr-hint">On an iPhone the recording stops if the phone locks or Jarvis goes to the background - keep this screen in front.</p>
            )}
            {isCall && captureNote && <p className="rr-capture-warn" role="alert"><Icon name="flag" size={14} /><span>{captureNote}</span></p>}
          </section>
          {details}

          <div className="rr-foot rr-foot-live">
            {phase === 'recording' && (
              <button type="button" className="rr-big rr-pause" onClick={pause} aria-label="Pause for a break">
                <span className="rr-big-glyph" aria-hidden="true"><span /><span /></span>
                <span className="rr-big-word">PAUSE</span>
              </button>
            )}
            {phase === 'paused' && (
              <button type="button" className="rr-big rr-resume" onClick={resume} aria-label="Resume recording">
                <span className="rr-big-glyph is-play" aria-hidden="true" />
                <span className="rr-big-word">RESUME</span>
              </button>
            )}
            {phase === 'stopping' && (
              <p className="rr-big-words rr-finishing" role="status">Saving the last part…</p>
            )}
            <button type="button" className="rr-stop" onClick={() => setConfirming(true)} disabled={phase === 'stopping'}>
              <span className="rr-stop-square" aria-hidden="true" /> STOP
            </button>
          </div>
        </>
      )}

      {/* ------------------------------------------------------- after */}
      {phase === 'done' && (
        <>
          <section className="rr-card rr-after" role="status">
            {Array.isArray(after?.segments) && after.segments.length > 0 ? (
              <>
                <h2 className="rr-h2">The transcript is ready</h2>
                <p className="rr-hint">Put names to the voices while you remember who sat where.</p>
              </>
            ) : /Failed|No transcript/.test(transcriptWords(after)) ? (
              <>
                <h2 className="rr-h2">Recorded and saved</h2>
                <p className="rr-hint">The transcript could not be written on this server; the recording itself can be played.</p>
              </>
            ) : (
              <>
                <h2 className="rr-h2">Transcribing…</h2>
                <p className="rr-hint">You can close this; we&rsquo;ll tell you when it&rsquo;s ready. {up.savedToMs > 0 && `${clockOf(up.savedToMs)} recorded.`}</p>
              </>
            )}
            <div className="rr-after-links">
              {recordingId && (
                <Link className="btn btn-outline tds-btn rr-open" to={`/recordings/${recordingId}`}>Open the recording</Link>
              )}
              {meeting?.id && (
                <Link className="btn btn-outline tds-btn rr-open rr-open-meeting" to={`/meetings/${meeting.id}`}>Open the meeting</Link>
              )}
            </div>
          </section>
          {details}
          {Array.isArray(after?.segments) && after.segments.length > 0 && (
            <SpeakerNaming recording={after}
              onRenamed={(map) => setAfter((r) => ({ ...r, segments: (r.segments || []).map((s) => {
                const to = map[s.speaker_id] || map[s.speaker];
                return to ? { ...s, speaker: to } : s;
              }) }))} />
          )}
        </>
      )}

      {confirming && (
        <Dialog label="Stop recording?" onClose={() => setConfirming(false)} className="rr-confirm">
          <p className="tds-quiet">
            The recording ends here and is transcribed. {clockOf(clock)} recorded. For a break, use Pause instead.
          </p>
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" onClick={() => setConfirming(false)}>Keep recording</button>
            <button type="button" className="btn btn-primary tds-btn rr-confirm-stop" onClick={stop}>Stop</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
